// Présentation des sources citées (écran, exports, courrier PDF / HTML) — partagée par le serveur et l'interface.
// Un classeur se localise par « feuille « X », ligne N » : jamais par « page 1 » (la « page » d'un classeur est sa feuille).
// Les extraits de tableau sont AFFICHÉS avec des montants au format français (« 1 234,50 € ») ; l'extrait stocké n'est jamais modifié.

const INSECABLE = '\u00a0';

/** Localisation d'un classeur (« feuille « Devis », ligne 16 » ou « …, lignes 15 à 17 »). */
export const estLocalisationClasseur = (locator: string | null | undefined) => /^feuille\b/i.test((locator ?? '').trim());

/** « page 4, § 5.2 » pour un PDF ; « feuille « Devis », ligne 16 » pour un classeur (sans « page »). */
export function localisationSource(page: number | null | undefined, locator: string | null | undefined, prefixePage = 'page ') {
  const loc = locator && locator !== 'demande saisie' ? locator : null;
  if (estLocalisationClasseur(loc)) return loc!;
  return [page ? `${prefixePage}${page}` : null, loc].filter(Boolean).join(', ');
}

/** Groupe les milliers par une espace insécable : 123456 → « 123 456 ». */
function grouper(entier: string) {
  return entier.replace(/\B(?=(\d{3})+(?!\d))/g, INSECABLE);
}

/** Montant en euros au format français, deux décimales : « 1 234,50 € ». Entrée : nombre ou chaîne décimale « 1234.5 ». */
export function montantFr(v: number | string): string {
  const s = typeof v === 'number' ? v.toFixed(2) : v;
  const m = String(s).trim().match(/^(-?)(\d+)(?:\.(\d+))?$/);
  if (!m) return String(v);
  // Arrondi au centime par la chaîne (pas de flottant) : on ne touche qu'aux décimales au-delà de la 2e.
  let ent = m[2]; let dec = (m[3] ?? '').padEnd(3, '0');
  let cent = Number(dec.slice(0, 2));
  if (Number(dec[2]) >= 5) {
    cent += 1;
    if (cent === 100) { cent = 0; ent = (BigInt(ent) + 1n).toString(); }
  }
  dec = String(cent).padStart(2, '0');
  return `${m[1]}${grouper(ent)},${dec}${INSECABLE}€`;
}

/** Nombre au format français (virgule décimale ; milliers groupés à partir de 10 000). */
export function nombreFr(s: string): string {
  const m = s.match(/^(-?)(\d+)(?:\.(\d+))?$/);
  if (!m) return s;
  const ent = m[2].length > 4 ? grouper(m[2]) : m[2];
  return `${m[1]}${ent}${m[3] ? `,${m[3]}` : ''}`;
}

const NUMERIQUE = /^-?\d+(?:\.\d+)?$/;
const UNITE = /^(u|u\.|un|ens|ensemble|ft|forfait|ml|m|m²|m2|m³|m3|kg|t|h|j|pce|pcs|lot|l)$/i;
const LIBELLE_MONTANT = /total|montant|\btva\b|\bttc\b|\bht\b|prix|€/i;

/**
 * Ligne d'un extrait de tableau (« EX-02 | Désignation | Ens | 1 | 1234.5 | 1234.5 ») : prix unitaire et montant en euros,
 * quantités en nombre français. Une ligne de texte (sans « | ») est rendue telle quelle.
 */
export function ligneAffichee(ligne: string): string {
  if (!ligne.includes(' | ')) return ligne;
  const cellules = ligne.split(' | ');
  const num = cellules.map((c, i) => (NUMERIQUE.test(c.trim()) ? i : -1)).filter((i) => i >= 0);
  if (!num.length) return ligne;
  const texte = cellules.filter((_, i) => !num.includes(i)).join(' ');
  const monnaie = new Set<number>();
  if (num.length >= 3) { monnaie.add(num[num.length - 1]); monnaie.add(num[num.length - 2]); }
  else if (num.length === 2 && (cellules.slice(0, num[0]).some((c) => UNITE.test(c.trim())) || LIBELLE_MONTANT.test(texte))) monnaie.add(num[1]);
  else if (num.length === 1 && LIBELLE_MONTANT.test(texte)) monnaie.add(num[0]);
  return cellules.map((c, i) => {
    if (!num.includes(i)) return c;
    return monnaie.has(i) ? montantFr(c.trim()) : nombreFr(c.trim());
  }).join(' | ');
}

/** Extrait affiché : chaque ligne de tableau mise au format français ; le reste inchangé. */
export function extraitAffiche(t: string | null | undefined): string {
  if (!t) return '';
  return t.split('\n').map(ligneAffichee).join('\n');
}
